import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, registerUnauthorizedHandler, setAuthToken } from '@/lib/api'
import type {
  AuthUser,
  LoginResponse,
  LoginSuccessResponse,
  MfaSetupResponse,
  MfaStatus,
  RegisterPayload,
} from '@/types/auth'

const TOKEN_STORAGE_KEY = 'goodrh_token'

interface AuthContextValue {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<LoginResponse>
  verifyMfa: (code: string, mfaToken: string) => Promise<LoginSuccessResponse>
  register: (payload: RegisterPayload) => Promise<LoginSuccessResponse>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  fetchMfaStatus: () => Promise<MfaStatus>
  setupMfa: () => Promise<MfaSetupResponse>
  confirmMfaSetup: (code: string) => Promise<void>
  disableMfa: (code: string) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function persistSession(token: string) {
  localStorage.setItem(TOKEN_STORAGE_KEY, token)
  setAuthToken(token)
}

function clearSession() {
  localStorage.removeItem(TOKEN_STORAGE_KEY)
  setAuthToken(null)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    registerUnauthorizedHandler(() => {
      clearSession()
      setUser(null)
    })
  }, [])

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY)

    if (!token) {
      setIsLoading(false)
      return
    }

    setAuthToken(token)

    api
      .get<AuthUser>('/auth/user')
      .then((response) => setUser(response.data))
      .catch(() => clearSession())
      .finally(() => setIsLoading(false))
  }, [])

  const login = useCallback(async (email: string, password: string): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', { email, password })

    if ('mfa_required' in response.data) {
      return response.data
    }

    persistSession(response.data.token)
    setUser(response.data.user)

    return response.data
  }, [])

  const verifyMfa = useCallback(async (code: string, mfaToken: string): Promise<LoginSuccessResponse> => {
    const response = await api.post<LoginSuccessResponse>(
      '/auth/mfa/verify',
      { code },
      { headers: { Authorization: `Bearer ${mfaToken}` } },
    )

    persistSession(response.data.token)
    setUser(response.data.user)

    return response.data
  }, [])

  const register = useCallback(async (payload: RegisterPayload): Promise<LoginSuccessResponse> => {
    const response = await api.post<LoginSuccessResponse>('/auth/register', payload)

    persistSession(response.data.token)
    setUser(response.data.user)

    return response.data
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } catch {
      // Best-effort: still clear local session even if the request fails
      // (e.g. token already expired server-side).
    } finally {
      clearSession()
      setUser(null)
    }
  }, [])

  const refreshUser = useCallback(async () => {
    const response = await api.get<AuthUser>('/auth/user')
    setUser(response.data)
  }, [])

  const fetchMfaStatus = useCallback(async () => {
    const response = await api.get<MfaStatus>('/auth/mfa/status')
    return response.data
  }, [])

  const setupMfa = useCallback(async () => {
    const response = await api.post<MfaSetupResponse>('/auth/mfa/setup')
    return response.data
  }, [])

  const confirmMfaSetup = useCallback(
    async (code: string) => {
      await api.post('/auth/mfa/verify', { code })
      await refreshUser()
    },
    [refreshUser],
  )

  const disableMfa = useCallback(
    async (code: string) => {
      await api.post('/auth/mfa/disable', { code })
      await refreshUser()
    },
    [refreshUser],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      login,
      verifyMfa,
      register,
      logout,
      refreshUser,
      fetchMfaStatus,
      setupMfa,
      confirmMfaSetup,
      disableMfa,
    }),
    [user, isLoading, login, verifyMfa, register, logout, refreshUser, fetchMfaStatus, setupMfa, confirmMfaSetup, disableMfa],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
